function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _taggedTemplateLiteral(strings, raw) {
    if (!raw) {
        raw = strings.slice(0);
    }
    return Object.freeze(Object.defineProperties(strings, {
        raw: {
            value: Object.freeze(raw)
        }
    }));
}
function _templateObject() {
    var data = _taggedTemplateLiteral([
        "\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n  align-items: center;\n  position: fixed;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n  z-index: ",
        ";\n  will-change: opacity;\n  opacity: 0;\n  &.appear {\n    animation: ",
        " 0.3s ease-in-out forwards;\n  }\n  &.disappear {\n    animation: ",
        " 0.3s ease-in-out forwards;\n  }\n"
    ]);
    _templateObject = function _templateObject() {
        return data;
    };
    return data;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion";
import React, { createContext, useEffect, useRef, useState } from "react";
import styled, { ThemeProvider } from "styled-components";
import { Overlay } from "../../components/Overlay";
import { animationHandler, animationMap, animationVariants, appearAnimation, disappearAnimation } from "../../util/animationToolkit";
var ModalWrapper = styled(m.div).withConfig({
    componentId: "sc-8ae17773-0"
})(_templateObject(), function(param) {
    var theme = param.theme;
    return theme.zIndices.modal - 1;
}, appearAnimation, disappearAnimation);
export var Context = /*#__PURE__*/ createContext({
    isOpen: false,
    nodeId: "",
    modalNode: null,
    setModalNode: function() {
        return null;
    },
    onPresent: function() {
        return null;
    },
    onDismiss: function() {
        return null;
    }
});
var ModalProvider = function(param) {
    var children = param.children, modalTheme = param.modalTheme;
    var ref4 = useState(false), isOpen = ref4[0], setIsOpen = ref4[1];
    var ref1 = useState(), modalNode = ref1[0], setModalNode = ref1[1];
    var ref2 = useState(""), nodeId = ref2[0], setNodeId = ref2[1];
    var ref3 = useState(true), closeOnOverlayClick = ref3[0], setCloseOnOverlayClick = ref3[1];
    var animationRef = useRef(null);
    var handlePresent = function(node, newNodeId, closeOverlayClick) {
        setModalNode(node);
        setIsOpen(true);
        setNodeId(newNodeId);
        setCloseOnOverlayClick(closeOverlayClick);
    };
    var handleDismiss = function() {
        setModalNode(undefined);
        setIsOpen(false);
        setNodeId("");
        setCloseOnOverlayClick(true);
    };
    useEffect(function() {
        if (!isOpen) return undefined;
        var previousFocus = document.activeElement;
        var previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        var focusable = function() {
            var ref;
            return Array.from(((ref = animationRef.current) === null || ref === void 0 ? void 0 : ref.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')) || []).filter(function(element) {
                return element.getClientRects().length > 0;
            });
        };
        var frame = requestAnimationFrame(function() {
            var ref, ref5;
            if (!((ref = animationRef.current) === null || ref === void 0 ? void 0 : ref.contains(document.activeElement))) (ref5 = focusable()[0]) === null || ref5 === void 0 ? void 0 : ref5.focus();
        });
        var onKeyDown = function(event) {
            var ref, ref6;
            if (event.key === "Escape" && closeOnOverlayClick) handleDismiss();
            if (event.key !== "Tab") return;
            var elements = focusable();
            var first = elements[0];
            var last = elements[elements.length - 1];
            if (!first) {
                event.preventDefault();
                return;
            }
            if (event.shiftKey && (document.activeElement === first || !((ref = animationRef.current) === null || ref === void 0 ? void 0 : ref.contains(document.activeElement)))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !((ref6 = animationRef.current) === null || ref6 === void 0 ? void 0 : ref6.contains(document.activeElement)))) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return function() {
            cancelAnimationFrame(frame);
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", onKeyDown);
            if (previousFocus === null || previousFocus === void 0 ? void 0 : previousFocus.isConnected) previousFocus.focus();
        };
    }, [
        isOpen,
        closeOnOverlayClick
    ]);
    var handleOverlayDismiss = function() {
        if (closeOnOverlayClick) {
            handleDismiss();
        }
    };
    return /*#__PURE__*/ _jsxs(Context.Provider, {
        value: {
            isOpen: isOpen,
            nodeId: nodeId,
            modalNode: modalNode,
            setModalNode: setModalNode,
            onPresent: handlePresent,
            onDismiss: handleDismiss
        },
        children: [
            /*#__PURE__*/ _jsx(LazyMotion, {
                features: domAnimation,
                children: /*#__PURE__*/ _jsx(AnimatePresence, {
                    children: isOpen && /*#__PURE__*/ _jsx(ThemeProvider, {
                        theme: function(outerTheme) {
                            return modalTheme || outerTheme;
                        },
                        children: /*#__PURE__*/ _jsxs(ModalWrapper, _objectSpread({
                            className: "exchange-ui exchange-dialog-root",
                            ref: animationRef,
                            onAnimationStart: function() {
                                return animationHandler(animationRef.current);
                            }
                        }, animationMap, {
                            variants: animationVariants,
                            transition: {
                                duration: 0.3
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Overlay, {
                                    onClick: handleOverlayDismiss
                                }),
                                /*#__PURE__*/ React.isValidElement(modalNode) && /*#__PURE__*/ React.cloneElement(modalNode, {
                                    onDismiss: handleDismiss
                                })
                            ]
                        }))
                    })
                })
            }),
            children
        ]
    });
};
export default ModalProvider;
